import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Form,
  Image,
  Input,
  message,
  Upload,
  Select,
  Typography,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Router from "..";
import { fileToBase64 } from "../utils/fileToBase64";
import { useUserStore } from "../../stores/userStore";
import { CATEGORY_MAPPING } from "../utils/categories";

export const BrandForm = ({ form }) => {
  const { userInfo, loadUserInfo } = useUserStore();
  const [isDisabled, setIsDisabled] = useState(true);
  const [registrationImg, setRegistrationImg] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRegistrationNumberValid, setIsRegistrationNumberValid] =
    useState(false);
  const [initialRegistrationNumber, setInitialRegistrationNumber] =
    useState("");

  const isKorea = userInfo?.user_info?.country === "KR";
  const isNewBrand = !userInfo?.type_info;
  const isMaster = userInfo?.type_info?.grade === "master";
  const canEdit = isNewBrand || isMaster;

  const companyLabel =
    userInfo?.user_info?.user_type === "agency" ? "에이전시" : "브랜드";

  const normalizeRegistrationNumber = (value) => {
    const nextValue = value || "";
    return isKorea ? nextValue.replace(/[^0-9]/g, "") : nextValue.slice(0, 50);
  };

  const handleRegistrationNumberChange = (e) => {
    const nextValue = normalizeRegistrationNumber(e.target.value);
    const isSameAsOriginal =
      nextValue === initialRegistrationNumber &&
      initialRegistrationNumber !== "";

    if (isKorea && isSameAsOriginal) {
      setIsRegistrationNumberValid(true);
    } else {
      setIsRegistrationNumberValid(false);
    }
  };

  useEffect(() => {
    if (userInfo?.type_info) {
      const regNum = userInfo.type_info.registration_number;
      form.setFieldsValue({
        company_name: userInfo.type_info.company_name,
        industry: userInfo.type_info.industry,
        registration_number: regNum,
      });
      setInitialRegistrationNumber(regNum);
      if (regNum) setIsRegistrationNumberValid(true);
    }

    // 브랜드 정보가 있을 때 기존 이미지 로드
    if (userInfo?.type_info?.registration_img) {
      setRegistrationImg(userInfo.type_info.registration_img);
    }
  }, [userInfo, form]);

  const handleSubmit = async () => {
    // 브랜드 생성
    if (isNewBrand) {
      const values = await form.validateFields([
        "company_name",
        "industry",
        "registration_number",
      ]);

      if (isKorea && !isRegistrationNumberValid) {
        message.error("사업자 번호 인증을 완료해주세요.");
        return;
      }
      if (!registrationImg) {
        message.error(
          "사업자 등록증 또는 W-9/Invoice 이미지를 업로드해주세요."
        );
        return;
      }

      const created = await Router.Api.createBrand({
        ...values,
        registration_img: registrationImg,
      });

      if (created) {
        setIsDisabled(true);
        setSelectedFile(null);
        loadUserInfo();
      }
      return;
    }

    // 브랜드 수정
    if (!isMaster) {
      message.error("수정 권한이 없습니다.");
      return;
    }

    const values = await form.validateFields([
      "company_name",
      "industry",
      "registration_number",
    ]);

    if (isKorea && !isRegistrationNumberValid) {
      message.error("사업자 번호 인증을 완료해주세요.");
      return;
    }
    if (!registrationImg) {
      message.error("사업자 등록증 사진을 업로드해주세요.");
      return;
    }

    const updated = await Router.Api.updateAccount({
      ...values,
      user_type: "brand",
      company_id: userInfo?.type_info?.company_id,
      registration_img: registrationImg,
    });

    if (updated) {
      setIsDisabled(true);
      setSelectedFile(null);
      loadUserInfo();
    }
  };

  const handleVerifyRegistrationNumber = async () => {
    const regNo = form.getFieldValue("registration_number");
    const result = await Router.Api.validateBusinessNumber({ b_no: regNo });
    if (result) setIsRegistrationNumberValid(true);
  };

  const handleCancel = () => {
    setIsDisabled(true);
    setSelectedFile(null);
    if (isNewBrand) {
      form.resetFields();
      setInitialRegistrationNumber("");
      setIsRegistrationNumberValid(false);
    } else {
      loadUserInfo();
    }
  };

  const handleFileSelect = async (file) => {
    const base64 = await fileToBase64(file);
    setRegistrationImg(base64);
    setSelectedFile(file);
    return false; // 자동 업로드 방지
  };

  const renderHeaderButtons = () => {
    if (isNewBrand) {
      // 브랜드 생성 모드
      if (isDisabled) return null;
      return (
        <>
          <Button
            htmlType="button"
            size="small"
            className="border-[#171717] text-[#171717]"
            onClick={handleCancel}
          >
            취소
          </Button>
          <Button
            type="primary"
            htmlType="button"
            size="small"
            className="bg-[#171717] hover:!bg-[#171717]"
            onClick={handleSubmit}
          >
            생성
          </Button>
        </>
      );
    } else {
      // 브랜드 수정 모드
      if (isDisabled) {
        return (
          canEdit && (
            <Button
              type="primary"
              htmlType="button"
              size="small"
              className="bg-[#171717] hover:!bg-[#171717]"
              onClick={() => setIsDisabled(false)}
            >
              수정
            </Button>
          )
        );
      } else {
        return (
          <>
            <Button
              htmlType="button"
              size="small"
              className="border-[#171717] text-[#171717] hover:!border-[#171717] hover:!text-[#171717]"
              onClick={handleCancel}
            >
              취소
            </Button>
            <Button
              type="primary"
              htmlType="button"
              size="small"
              className="bg-[#171717] hover:!bg-[#171717]"
              onClick={handleSubmit}
            >
              완료
            </Button>
          </>
        );
      }
    }
  };

  const renderGradeBadge = () => {
    if (!userInfo?.type_info) return null;

    const gradeConfig = {
      master: { bg: "bg-[#E6F7ED]", text: "text-[#52C41A]", label: "master" },
      editor: { bg: "bg-[#E6F4FF]", text: "text-[#1890FF]", label: "editor" },
      viewer: { bg: "bg-[#FFF7E6]", text: "text-[#FAAD14]", label: "viewer" },
    };

    const config = gradeConfig[userInfo.type_info.grade] || gradeConfig.viewer;

    return (
      <div className="mb-[15px]">
        <span
          className={`px-3 py-1 rounded text-[12px] font-medium ${config.bg} ${config.text}`}
        >
          {config.label}
        </span>
      </div>
    );
  };

  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <div>
            {isNewBrand ? `${companyLabel} 생성` : `${companyLabel} 정보`}
          </div>
          <div className="flex gap-2 items-center">{renderHeaderButtons()}</div>
        </div>
      }
      className="w-full border border-[#d9d9d9] rounded-[10px] bg-white shadow-[0_2px_6px_rgba(0,0,0,0.1)] mb-[30px]"
    >
      {isNewBrand && isDisabled ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Typography.Text className="text-center text-gray-600">
            아직 등록된 정보가 없습니다.
            <br />
            {companyLabel}를 생성해야 캠페인을 등록할 수 있습니다.
          </Typography.Text>

          <Button
            type="primary"
            className="bg-[#171717] hover:!bg-[#171717] mt-4"
            onClick={() => setIsDisabled(false)}
          >
            {companyLabel} 생성하기
          </Button>
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          disabled={isDisabled}
          requiredMark={false}
        >
          {renderGradeBadge()}

          <Form.Item
            label="회사명"
            name="company_name"
            rules={[{ required: true, message: "회사명을 입력해주세요." }]}
          >
            <Input placeholder="회사명을 입력해주세요." />
          </Form.Item>

          <Form.Item label="산업군">
            {isDisabled ? (
              <Input
                disabled
                value={CATEGORY_MAPPING[userInfo?.type_info?.industry] || ""}
              />
            ) : (
              <Form.Item
                name="industry"
                noStyle
                rules={[{ required: true, message: "산업군을 선택해주세요." }]}
              >
                <Select placeholder="업종을 선택해주세요">
                  {Object.entries(CATEGORY_MAPPING).map(([value, label]) => (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form.Item>

          <Form.Item label="사업자 번호" required shouldUpdate={true}>
            {() => {
              const regNum = form.getFieldValue("registration_number");
              const isButtonDisabled =
                !regNum ||
                isRegistrationNumberValid ||
                (regNum === initialRegistrationNumber &&
                  initialRegistrationNumber !== "");
              return (
                <div className="flex gap-2 items-start">
                  <Form.Item
                    name="registration_number"
                    noStyle
                    rules={[
                      {
                        required: true,
                        message: "사업자 번호를 입력해주세요.",
                      },
                    ]}
                    normalize={normalizeRegistrationNumber}
                  >
                    <Input
                      placeholder="사업자 번호를 입력해주세요."
                      inputMode={isKorea ? "numeric" : "text"}
                      disabled={isDisabled}
                      onChange={handleRegistrationNumberChange}
                      style={{ flex: 1 }}
                    />
                  </Form.Item>

                  {isKorea && !isDisabled && (
                    <Button
                      type="button"
                      onClick={handleVerifyRegistrationNumber}
                      disabled={isButtonDisabled}
                      className={`text-white ${
                        isButtonDisabled
                          ? "bg-gray-300 cursor-not-allowed hover:!bg-gray-300"
                          : "bg-[#171717] hover:!bg-[#171717]"
                      }`}
                    >
                      인증
                    </Button>
                  )}
                </div>
              );
            }}
          </Form.Item>

          {!["editor", "viewer"].includes(userInfo?.type_info?.grade) && (
            <Form.Item
              label="사업자 등록증"
              rules={[
                {
                  required: true,
                  message:
                    "사업자 등록증 또는 W-9/Invoice 이미지로 업로드해주세요.",
                },
              ]}
            >
              <p className="text-[12px] text-gray-500 mb-4">
                글로벌의 경우, Invoice나 W-9를 이미지 파일로 제출해주세요.
              </p>
              <div className="flex gap-4 items-start">
                <Upload
                  listType="picture-card"
                  beforeUpload={handleFileSelect}
                  accept="image/*"
                  showUploadList={false}
                  disabled={isDisabled}
                >
                  <div>
                    <UploadOutlined />
                    <div>업로드</div>
                  </div>
                </Upload>

                {(selectedFile || registrationImg) && (
                  <div>
                    <Image
                      src={
                        selectedFile
                          ? URL.createObjectURL(selectedFile)
                          : registrationImg
                      }
                      alt="사업자 등록증"
                      width={102}
                      height={102}
                      className="rounded-lg object-cover border border-[#d9d9d9] cursor-pointer"
                      preview={{
                        mask: false,
                      }}
                    />
                  </div>
                )}
              </div>
            </Form.Item>
          )}
        </Form>
      )}
    </Card>
  );
};
